import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { TOAST } from '../utils/toastMessages';
import { MessageSquare, ArrowLeft } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const FeedbackPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const { preSelectedInquiry, inquiries: passedInquiries } = location.state || {};
  
  // Get user role from context or location state
  const userRole = location.state?.userRole || user?.role;

  const [inquiries, setInquiries] = useState(passedInquiries || []);
  const [formData, setFormData] = useState({
    category: preSelectedInquiry ? (userRole === 'customer' ? 'report_vendor' : 'report_customer') : 'bug',
    subject: '',
    description: '',
    inquiry_id: preSelectedInquiry?.id || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch inquiries if not passed
  useEffect(() => {
    const fetchInquiries = async () => {
      if (!passedInquiries && userRole) {
        try {
          const token = localStorage.getItem('token');
          const endpoint = userRole === 'customer' ? '/api/inquiries/customer' : '/api/inquiries/vendor';
          const response = await axios.get(`${API}${endpoint}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setInquiries(response.data);
        } catch (error) {
          console.error('Error fetching inquiries:', error);
        }
      }
    };

    fetchInquiries();
  }, [passedInquiries, userRole]);

  // Update form when preSelectedInquiry is provided
  useEffect(() => {
    if (preSelectedInquiry) {
      const reportingEntity = userRole === 'customer' 
        ? preSelectedInquiry.vendor_name || 'vendor'
        : preSelectedInquiry.customer_name || 'customer';
      
      setFormData({
        category: userRole === 'customer' ? 'report_vendor' : 'report_customer',
        subject: `Issue with ${reportingEntity} - ${preSelectedInquiry.event_type}`,
        description: '',
        inquiry_id: preSelectedInquiry.id
      });
    }
  }, [preSelectedInquiry, userRole]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate inquiry selection for report categories
    if ((formData.category === 'report_vendor' || formData.category === 'report_customer') && !formData.inquiry_id) {
      toast.error('Please select an inquiry to report');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Auto-capture page URL
      const page_url = window.location.href;
      
      // Auto-capture device and browser info
      const device_info = `${navigator.userAgent} | Screen: ${window.screen.width}x${window.screen.height} | Viewport: ${window.innerWidth}x${window.innerHeight}`;
      
      await axios.post(
        `${API}/feedback`,
        {
          ...formData,
          page_url,
          device_info
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(TOAST.FEEDBACK_SUBMITTED);
      
      // Navigate back to previous page
      navigate(-1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Navigation />
      <div className="min-h-screen-safe bg-gray-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <MessageSquare className="h-5 w-5 mr-2" />
                Send Feedback
              </CardTitle>
              <CardDescription>
                Help us improve by sharing your feedback, reporting bugs, or suggesting features
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData({ ...formData, category: value, inquiry_id: '' })}
                  >
                    <SelectTrigger id="category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bug">🐛 Bug Report</SelectItem>
                      <SelectItem value="feature">💡 Feature Request</SelectItem>
                      <SelectItem value="improvement">⚡ Improvement Suggestion</SelectItem>
                      {userRole === 'customer' && <SelectItem value="report_vendor">⚠️ Report Service Provider</SelectItem>}
                      {userRole === 'vendor' && <SelectItem value="report_customer">⚠️ Report Customer</SelectItem>}
                      <SelectItem value="other">💬 Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {(formData.category === 'report_vendor' || formData.category === 'report_customer') && inquiries.length > 0 && (
                  <div>
                    <Label htmlFor="inquiry">Select Inquiry *</Label>
                    <Select 
                      value={formData.inquiry_id} 
                      onValueChange={(value) => setFormData({ ...formData, inquiry_id: value })}
                    >
                      <SelectTrigger id="inquiry">
                        <SelectValue placeholder="Choose an inquiry" />
                      </SelectTrigger>
                      <SelectContent>
                        {inquiries.map((inquiry) => (
                          <SelectItem key={inquiry.id} value={inquiry.id}>
                            {userRole === 'customer' 
                              ? `${inquiry.vendor_name} - ${inquiry.event_type}` 
                              : `${inquiry.customer_name || 'Customer'} - ${inquiry.event_type}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Brief summary of your feedback"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Provide details about your feedback, bug report, or feature request..."
                    rows={6}
                    required
                  />
                </div>

                <div className="bg-gray-50 p-3 rounded text-xs text-gray-600">
                  <p className="font-medium mb-1">Auto-captured information:</p>
                  <p>• Page URL: {window.location.href}</p>
                  <p>• Browser: {navigator.userAgent.split(' ').slice(-2).join(' ')}</p>
                  <p>• Screen Size: {window.screen.width}×{window.screen.height}</p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting || !formData.subject.trim() || !formData.description.trim()}
                    className="flex-1"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(-1)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
      {!user && <Footer />}
    </>
  );
};

export default FeedbackPage;
